import { createSlice } from '@reduxjs/toolkit';

import SDK from '../Components/SDK';
import { getUserIdFromJid } from '../Helpers/Utility';
import { clearState } from './clearSlice';

export const KEPT_PAGE_SIZE = 10;
const KEPT_INDEX_PAGE_SIZE = 50;
const KEPT_INDEX_MAX_PAGES = 20;
// A chat with more keeps than one scan covers is not rescanned before this.
const KEPT_INDEX_RESCAN_MS = 5 * 60 * 1000;

const EMPTY_LIST = {
    messages: [],
    total: 0,
    next: '',
    isLoading: false,
    isLoaded: false,
    isIndexComplete: false,
    indexRetryAt: 0,
};

// One index scan per chat at a time; concurrent expiry checks share it.
const pendingIndexScans = new Map();

// byChatId holds the paged Kept Messages list; statusByMsgId holds the keep state learnt
// from the server or a push, which wins over the keptStatus a history page carried.
const initialState = {
    byChatId: {},
    statusByMsgId: {},
};

const keptListOf = (state, chatId) => {
    if (!state.byChatId[chatId]) state.byChatId[chatId] = { ...EMPTY_LIST };
    return state.byChatId[chatId];
};

const recordKept = (state, messages) => {
    messages.forEach(message => {
        state.statusByMsgId[message.msgId] = { isKept: true, keptBy: message.keptBy, keptAt: message.keptAt };
    });
};

const keptMessagesSlice = createSlice({
    name: 'keptMessages',
    initialState,
    reducers: {
        setKeptLoading(state, action) {
            const { chatId, isLoading } = action.payload || {};
            if (!chatId) return;
            keptListOf(state, chatId).isLoading = isLoading;
        },
        setKeptPage(state, action) {
            const { chatId, messages = [], total = 0, next = '', isFirstPage } = action.payload || {};
            if (!chatId) return;
            const keptList = keptListOf(state, chatId);
            const known = isFirstPage ? [] : keptList.messages;
            const knownIds = new Set(known.map(message => message.msgId));
            keptList.messages = known.concat(messages.filter(message => !knownIds.has(message.msgId)));
            keptList.total = Number(total) || 0;
            keptList.next = next || '';
            keptList.isLoading = false;
            keptList.isLoaded = true;
            recordKept(state, messages);
        },
        setKeptIndex(state, action) {
            const { chatId, messages = [] } = action.payload || {};
            if (!chatId) return;
            keptListOf(state, chatId).isIndexComplete = true;
            recordKept(state, messages);
        },
        setKeptIndexPartial(state, action) {
            const { chatId, messages = [], retryAt = 0 } = action.payload || {};
            if (!chatId) return;
            keptListOf(state, chatId).indexRetryAt = retryAt;
            recordKept(state, messages);
        },
        applyKeepChange(state, action) {
            const { chatId, isKept, keptBy, keptAt, msgIds = [] } = action.payload || {};
            if (!msgIds.length) return;
            msgIds.forEach(msgId => {
                state.statusByMsgId[msgId] = { isKept, keptBy: isKept ? keptBy : '', keptAt: isKept ? keptAt : 0 };
            });
            const keptList = chatId && state.byChatId[chatId];
            if (!keptList || isKept) return;
            const removed = new Set(msgIds);
            const remaining = keptList.messages.filter(message => !removed.has(message.msgId));
            keptList.total = Math.max(0, keptList.total - (keptList.messages.length - remaining.length));
            keptList.messages = remaining;
        },
        clearKeptMessages(state, action) {
            delete state.byChatId[action.payload];
        },
    },
    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
    },
});

export const { setKeptLoading, setKeptPage, setKeptIndex, setKeptIndexPartial, applyKeepChange, clearKeptMessages } =
    keptMessagesSlice.actions;

export const selectKeptList = (state, chatId) => state.keptMessages.byChatId[chatId] || EMPTY_LIST;

/**
 * Keep state of one message: a push or server answer first, then the history flag.
 *
 * @param {object} state Redux state.
 * @param {object} message Message with msgId and, from history, keptStatus/keptBy.
 * @returns {{isKept: boolean, keptBy: string}|undefined} Undefined when nothing is known yet.
 */
export const selectKeepState = (state, message) => {
    if (!message?.msgId) return undefined;
    const known = state.keptMessages.statusByMsgId[message.msgId];
    if (known) return { isKept: known.isKept, keptBy: known.keptBy || '' };
    if (message.keptStatus === undefined || message.keptStatus === null) return undefined;
    return { isKept: Number(message.keptStatus) === 1, keptBy: message.keptBy || '' };
};

/**
 * Loads one page of the Kept Messages list, newest keep first.
 *
 * @param {string} chatJid Conversation JID: the peer for 1-1, the group JID for a group.
 * @param {{isLoadMore?: boolean}} options Load the page after the last one fetched.
 */
export const fetchKeptMessages =
    (chatJid, { isLoadMore = false } = {}) =>
    async (dispatch, getState) => {
        if (!chatJid) return;
        const chatId = getUserIdFromJid(chatJid);
        const keptList = selectKeptList(getState(), chatId);
        if (keptList.isLoading || (isLoadMore && !keptList.next)) return;

        dispatch(setKeptLoading({ chatId, isLoading: true }));
        const response = await SDK.getKeptMessages(chatJid, {
            limit: KEPT_PAGE_SIZE,
            before: isLoadMore ? keptList.next : '',
        });
        if (response?.statusCode !== 200) {
            dispatch(setKeptLoading({ chatId, isLoading: false }));
            return;
        }
        const { messages = [], total = 0, next = '' } = response.data || {};
        dispatch(setKeptPage({ chatId, messages, total, next, isFirstPage: !isLoadMore }));
    };

/**
 * Learns every kept message of a chat, so an expiry timer can tell kept from not kept.
 *
 * @param {string} chatJid Conversation JID.
 * @returns {Promise<boolean>} False when the server could not be asked; callers must not delete.
 */
export const ensureKeptIndex = chatJid => async (dispatch, getState) => {
    if (!chatJid) return false;
    const chatId = getUserIdFromJid(chatJid);
    const keptList = selectKeptList(getState(), chatId);
    if (keptList.isIndexComplete) return true;
    if (Date.now() < keptList.indexRetryAt) return false;

    if (!pendingIndexScans.has(chatId)) {
        const scan = scanKeptIndex(chatJid, chatId, dispatch).finally(() => pendingIndexScans.delete(chatId));
        pendingIndexScans.set(chatId, scan);
    }
    return pendingIndexScans.get(chatId);
};

const scanKeptIndex = async (chatJid, chatId, dispatch) => {
    const kept = [];
    let before = '';
    for (let page = 0; page < KEPT_INDEX_MAX_PAGES; page += 1) {
        const response = await SDK.getKeptMessages(chatJid, { limit: KEPT_INDEX_PAGE_SIZE, before });
        if (response?.statusCode !== 200) return false;
        const { messages = [], next = '' } = response.data || {};
        kept.push(...messages);
        if (!next) {
            dispatch(setKeptIndex({ chatId, messages: kept }));
            return true;
        }
        before = next;
    }
    // A partial index cannot prove a message is not kept, so nothing may expire yet.
    dispatch(setKeptIndexPartial({ chatId, messages: kept, retryAt: Date.now() + KEPT_INDEX_RESCAN_MS }));
    return false;
};

export default keptMessagesSlice.reducer;
